import $ from 'jquery';
import { canUseFastRecruit } from '../TokenUtil';
import crud from './helpers/crud';
import EventDAO from './EventDAO';
import EventDataDAO from './EventDataDAO';
import GameDAO from './GameDAO';
import generateUrls from '../urls';
import GoogleDAO from './GoogleDAO';
import ImportDAO from './ImportDAO';
import join from 'url-join';
import JSOG from 'jsog';
import ProcessedGameDataDAO from './ProcessedGameDataDAO';
import Promise from 'bluebird';
import RecruitDAO from './RecruitDAO';
import ScheduleTokenDAO from './ScheduleTokenDAO';
import SchoolDAO from './SchoolDAO';
import SchoolSourceDAO from './SchoolSourceDAO';
import ScrapeJobsDAO from './ScrapeJobsDAO';
import { getError, servletPost, servletSave } from './helpers';
import StaffDAO from './StaffDAO';
import SupportMessageDAO from './SupportMessageDAO';
import { fallback } from '../cache-methods';
import { isBlank } from 'underscore.string';
import _ from 'underscore';
import { requestHeaders } from '../constants';
import { paramify } from '../Etc';
import { get, post } from '../../fetch';
import ListDAO from './ListDAO';

export default class DAO {
  constructor({ accessToken, apiUrl, fastServerUrl }) {
    // Base properties of the DAO, set upon initializing the Session
    this.accessToken = accessToken;
    this.urls = generateUrls(apiUrl, fastServerUrl);

    // API Access properties
    const { urls } = this;
    this.events = new EventDAO(urls);
    this.eventData = new EventDataDAO(urls);
    this.courts = new crud(urls.COURTS_URL);
    this.divisions = new crud(urls.DIVISIONS_URL);
    this.followedTeams = new crud(urls.FOLLOWED_TEAMS_URL);
    this.fields = new crud(urls.FIELDS_URL);
    this.fieldValues = new crud(urls.FIELD_VALUES_URL);
    this.games = new GameDAO(urls);
    this.gameGroups = new crud(urls.GAME_GROUPS_URL);
    this.google = new GoogleDAO(urls);
    this.imports = new ImportDAO(urls);
    this.leagues = new crud(urls.LEAGUES_URL);
    this.lists = new ListDAO(urls.LISTS_URL);
    this.locationAliases = new crud(urls.LOCATION_ALIASES_URL);
    this.missingSchoolInfo = new crud(urls.MISSING_SCHOOL_INFO_URL);
    this.notes = new crud(urls.NOTES_URL);
    this.processedGameData = new ProcessedGameDataDAO(urls);
    this.recruits = new RecruitDAO(urls);
    this.schoolAliases = new crud(urls.SCHOOL_ALIASES_URL);
    this.schoolBrackets = new crud(urls.SCHOOL_BRACKETS_URL);
    this.scheduleInfo = new crud(urls.SCHEDULE_INFO_URL);
    this.scheduleTokens = new ScheduleTokenDAO(urls);
    this.scheduleValidations = new crud(urls.SCHEDULE_VALIDATIONS);
    this.schools = new SchoolDAO(urls);
    this.schoolSources = new SchoolSourceDAO(urls);
    this.schoolGames = new crud(urls.SCHOOL_GAMES_URL);
    this.scrapeJobs = new ScrapeJobsDAO(urls);
    this.sessions = new crud(urls.SESSIONS_URL);
    this.sourceSuppressions = new crud(urls.SOURCE_SUPPRESSIONS_URL);
    this.supportMessage = new SupportMessageDAO(urls);
    this.staff = new StaffDAO(urls);
    this.tasks = new crud(urls.TASKS_URL);
    this.tourneyAppointments = new crud(urls.TOURNEY_APPOINTMENTS_URL);
    this.tasks = new crud(urls.TASKS_URL);
    this.tourneys = new crud(urls.TOURNEYS_URL);
    this.tourneyTeams = new crud(urls.TOURNEY_TEAMS_URL);
    this.users = new StaffDAO(urls);
    this.userTourneys = new crud(urls.USER_TOURNEYS_URL);
    this.venues = new crud(urls.VENUES_URL);
    this.notifications = new crud(urls.NOTIFICATIONS_URL);
    this.notificationRecipients = new crud(urls.NOTIFICATION_RECIPIENTS_URL);
  }

  setAccessToken(accessToken) {
    this.accessToken = accessToken;
  }

  checkToken(token) {
    if (typeof token !== 'string' || isBlank(token)) {
      return Promise.reject(new Error('Invalid or empty token'));
    }
    token = token.trim();

    return fetch(join(this.urls.BASE_TOKEN_URL, token))
      .then(
        res => {
          if (res.ok) {
            return res.json();
          }
          throw new Error('Failed to get token information');
        }
      )
      .then(json => {
        const user = JSOG.decode(json);
        if (!canUseFastRecruit(user)) {
          throw new Error('Expired FastRecruit license');
        } else {
          this.setAccessToken(user.accessToken);
          return user;
        }
      });
  }

  /**
   * Log the user in and return a token if successful
   *
   * @param username user email
   * @param password user password
   * @returns Promise that resolves to token or rejects with error
   */
  login({ username, password }) {
    if (typeof username !== 'string' || isBlank(username)) {
      return Promise.reject(new Error('Username is required.'));
    }
    if (typeof password !== 'string' || isBlank(password)) {
      return Promise.reject(new Error('Password is required.'));
    }

    username = username.trim();
    password = password.trim();

    return servletSave(this.urls.LOGIN_URL, { username, password })
      .then(
        user => {
          if (!canUseFastRecruit(user)) {
            throw new Error('You do not have an active FastRecruit license.');
          }
          this.setAccessToken(user.accessToken);
          return user;
        }
      );
  }

  resetPassword({ userId, password }) {
    if (typeof password !== 'string' || isBlank(password)) {
      return Promise.reject(new Error('Password is required.'));
    }

    const accessToken = this.accessToken;

    return new Promise((resolve, reject) => {
      post({
        accessToken,
        responseType: 'xml',
        params: { password: password.trim() },
        url: join(this.urls.ACCOUNT_USERS_URL, `u-${userId}`, 'password'),
      })
        .then(resolve)
        .catch(reject);
    });
  }

  /**
   *  create report for missing school
   * @param placeId {String}
   * @param name {String}
   * @param rest {*}
   */
  reportMissingSchool({ placeId, name, ...rest }) {
    if (typeof placeId !== 'string' || isBlank(placeId)) {
      return Promise.reject(new Error('Place ID is required'));
    }

    if (typeof name !== 'string' || isBlank(name)) {
      return Promise.reject(new Error('Place name is required'));
    }

    return servletSave(this.urls.REPORT_MISSING_SCHOOL_URL, { placeId, name, ...rest });
  }

  swapTeams({ teamOne, teamTwo }) {
    if (teamOne === null || teamTwo === null) {
      return Promise.reject(new Error('Team one and team two are both required.'));
    }

    if (teamOne.id === teamTwo.id) {
      return Promise.reject(new Error('Team one cannot be the same as team two.'));
    }

    return servletSave(this.urls.TOURNEY_TEAMS_SWAP_URL, { teamOneId: teamOne.id, teamTwoId: teamTwo.id });
  }

  /**
   * Follow a team for a user ID, team ID, and account ID
   * @param userId ID of follower
   * @param teamId team ID to follow
   * @param accountId ID of account to follow for
   * @returns {*}
   */
  followTeam({ userId, teamId, accountId }) {
    return servletSave(this.urls.FOLLOWED_TEAMS_URL, { userId, team: { id: teamId }, accountId });
  }

  /**
   * Assign users to a tourney
   * @param accountId account ID for assignments
   * @param tourneyId tourney ID for assignments
   * @param dates a hash of user IDs to start and end dates,
   e.g. {'xxx-user-id': { start: '2016-06-01', end: '2016-06-02'}}
   * @returns {*}
   */
  assignUserTourneys({ accountId, tourneyId, dates }) {
    return new Promise((resolve, reject) => {
      $.post({
        url: join(this.urls.USER_TOURNEYS_URL, 'assign'),
        data: JSON.stringify({ accountId, tourneyId, dates }),
        contentType: 'application/json'
      }).then(
        saved => resolve(JSOG.decode(saved)),
        jqXhr => reject(getError(jqXhr))
      );
    });
  }

  /**
   * Assign followed teams for a tourney
   * @param accountId account ID for assignments
   * @param tourneyId tourney ID for assignments
   * @param teams a hash of team IDs to the set of users following them
   e.g. {'uuid': ['user0','user1']}
   * @returns {*}
   */
  assignFollowedTeams({ accountId, tourneyId, teams }) {
    return new Promise((resolve, reject) => {
      $.post({
        url: this.urls.ASSIGN_FOLLOWED_TEAMS_URL,
        data: JSON.stringify({ accountId, tourneyId, teams }),
        contentType: 'application/json'
      }).then(
        () => resolve(),
        jqXhr => reject(getError(jqXhr))
      );
    });
  }

  /**
   * Update the games in a tourney ID with the games and venues data
   * @param games new game data
   * @param venues new venue data
   * @param appointments new appointment data
   * @param tourneyId the id of the tourney being updated
   * @param lastFetch the last time games were fetched, used to prevent
   overwriting other user's edits
   */
  bulkUpdateGames({ games, venues, appointments, tourneyId, lastFetch }) {
    const gamesWithType = _.map(games, (game) => Object.assign({}, game, { eventType: 'Game' }));
    const appointmenstWithType = _.map(appointments, (appointment) => {
      Object.assign({}, appointment, { eventType: 'TourneyAppointment' });
    });
    const params = { games: gamesWithType, appointments: appointmenstWithType, venues, tourneyId, lastFetch };

    return servletSave(this.urls.TOURNEYS_BULK_UPDATE_URL, params);
  }

  /**
   * POST an import profile
   * @param importProfile
   * @returns {*}
   */
  createImportProfile(importProfile) {
    return servletPost(this.urls.RECRUITS_PROFILES_URL, importProfile);
  }

  uploadData({ accountId, mimeType, base64data }) {
    return new Promise((resolve, reject) => {
      $.ajax({
        url: this.urls.MEDIA_ATTACHMENTS_UPLOAD,
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({ accountId, mimeType, base64data })
      }).then(
        (resp) => resolve(JSOG.decode(resp)),
        (jqXhr) => reject(getError(jqXhr, 'Failed to upload photo'))
      );
    });
  }

  uploadPhoto({ formData }) {
    return new Promise((resolve, reject) => {
      $.ajax({
        url: this.urls.MEDIA_ATTACHMENTS_UPLOAD,
        type: 'POST',
        data: formData,
        processData: false,  // tell jQuery not to process the data
        contentType: false   // tell jQuery not to set contentType
      }).then(
        (resp) => resolve(JSOG.decode(resp)),
        (jqXhr) => reject(getError(jqXhr, 'Failed to upload photo'))
      );
    });
  }

  /**
   * Gets a list of fields that can be imported for a recruit
   */
  getRecruitImportFields() {
    return new Promise((resolve, reject) => {
      $.get({
        url: this.urls.RECRUITS_COLUMNS_URL,
        beforeSend: fallback
      }).then((destFields) => {
        resolve(JSOG.decode(destFields));
      }, (jqXhr) => {
        reject(getError(jqXhr, 'Failed to get destination fields'));
      });
    });
  }

  updateGameScore({ gameId, homeScore, visitorScore }) {
    if (typeof gameId !== 'string') {
      return Promise.reject(new Error('Invalid game ID'));
    }

    return new Promise((resolve, reject) => {
      $.post({
        url: this.urls.UPDATE_SCORE_URL,
        contentType: 'application/json',
        data: JSON.stringify({ gameId, homeScore, visitorScore })
      }).then(
        () => resolve(),
        jqXhr => reject(getError(jqXhr, 'Failed to update game score'))
      );
    });
  }

  calculatePoolStandings({ poolIds, preview = false }) {
    if (!_.isArray(poolIds) || !_.all(poolIds, id => typeof id === 'string')) {
      return Promise.reject(new Error('Invalid pool IDs'));
    }

    if (typeof preview !== 'boolean') {
      return Promise.reject(new Error('Invalid preview value'));
    }

    if (poolIds.length === 0) {
      return Promise.resolve([]);
    }

    return new Promise((resolve, reject) => {
      $.post({
        url: this.urls.CALCULATE_POOL_STANDINGS_URL,
        contentType: 'application/json',
        data: JSON.stringify({ poolIds, preview })
      }).then(
        standings => resolve(JSOG.decode(standings)),
        jqXhr => reject(getError(jqXhr, 'Failed to calculate pool standings'))
      );
    });
  }

  seedBrackets({ bracketIds, preview = false }) {
    if (!_.isArray(bracketIds) || !_.all(bracketIds, id => typeof id === 'string')) {
      return Promise.reject(new Error('Invalid pool IDs'));
    }

    if (typeof preview !== 'boolean') {
      return Promise.reject(new Error('Invalid preview value'));
    }

    if (bracketIds.length === 0) {
      return Promise.resolve([]);
    }

    return new Promise((resolve, reject) => {
      $.post({
        url: this.urls.SEED_BRACKETS_URL,
        contentType: 'application/json',
        data: JSON.stringify({ bracketIds, preview })
      }).then(
        standings => resolve(JSOG.decode(standings)),
        jqXhr => reject(getError(jqXhr, 'Failed to seed brackets'))
      );
    });
  }

  /**
   * Get the social feed
   */
  socialFeed(params = {}) {
    const accessToken = this.accessToken;

    return new Promise((resolve, reject) => {
      return get({ accessToken, url: this.urls.SOCIAL_FEED_URL, params })
        .then(({ results, headers }) => {
          const scrollId = headers.get(requestHeaders.SCROLL_ID);
          const totalCount = +headers.get(requestHeaders.TOTAL_HITS);

          resolve({ scrollId, totalCount, results: JSOG.decode(results) });
        })
        .catch(error => reject(getError(error, 'Failed to get recruit feed')));
    });
  }

  /**
   * Upload the formData and get a list of columns from the csv contained in the
   file attribute of the form data
   * @param formData
   * @returns {*}
   */
  getColumns({ formData = null }) {
    return new Promise((resolve, reject) => {
      if (formData === null) {
        reject(new Error('FormData is required'));
      }

      $.ajax({
        url: this.urls.RECRUITS_COLUMNS_URL,
        data: formData,
        processData: false,
        contentType: false,
        type: 'POST'
      }).then((cols) => {
        resolve(cols);
      }, (jqXhr) => {
        reject(getError(jqXhr, 'Failed to fetch columns for CSV'));
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/js/util/dao/index.js